import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from './App';
import { theme } from './theme';
import { initPerformanceMonitoring } from './utils/performanceMonitor';
import './index.css';

// Unregister any stale service workers to prevent caching issues
// This fixes flickering caused by old SW returning stale cached responses
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister().then((success) => {
        if (success && import.meta.env.DEV) {
          console.log('[SW] Unregistered stale service worker');
        }
      });
    }
  }).catch((err) => {
    console.warn('[SW] Failed to unregister service workers:', err);
  });
}

// Initialize performance monitoring after render to avoid blocking
// Defer to requestIdleCallback or setTimeout for non-critical init
const initPerfMonitoring = () => {
  initPerformanceMonitoring(
    (metric) => {
      if (import.meta.env.DEV) {
        console.log(`[Web Vital] ${metric.name}: ${metric.value.toFixed(2)}`);
      }
    },
    import.meta.env.PROD
  );
};

if ('requestIdleCallback' in window) {
  (window as any).requestIdleCallback(initPerfMonitoring);
} else {
  setTimeout(initPerfMonitoring, 0);
}

// Create React Query client with optimized defaults for offline support
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer for offline
      refetchOnWindowFocus: false, // Don't refetch on window focus
      retry: navigator.onLine ? 1 : 0, // Don't retry when offline
      networkMode: 'offlineFirst', // Return cached data first
    },
  },
});

// Service worker disabled in development to prevent reload loops
// Will be re-enabled for production builds

// Handle online/offline events to adjust query client behavior
window.addEventListener('online', () => {
  console.log('Network: Online');
  queryClient.setDefaultOptions({
    queries: {
      ...queryClient.getDefaultOptions().queries,
      retry: 1,
    },
  });
  queryClient.invalidateQueries();
});

window.addEventListener('offline', () => {
  console.log('Network: Offline');
  queryClient.setDefaultOptions({
    queries: {
      ...queryClient.getDefaultOptions().queries,
      retry: 0,
    },
  });
});

// Check if running in E2E test mode
const isE2EMode = typeof window !== 'undefined' && localStorage.getItem('e2e_test_mode') === 'true';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
        {/* Disable ReactQueryDevtools completely to prevent it from blocking clicks */}
        {/* {!isE2EMode && <ReactQueryDevtools initialIsOpen={false} />} */}
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
